import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import EditProductView from "./EditProductView";
import { FETCH_PRODUCT_DETAIL } from "./graphql";

const ProductLoader = ({ id, onSaved, hide, duplicateProduct }) => {
  const { loading, error, data } = useQuery(FETCH_PRODUCT_DETAIL, {
    variables: { id },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const product = data.product;

  const initialValues = {
    company: product.company,

    name: product.name,
    nameCn: product.nameCn,
    number: product.number,
    version: product.version,
    countryCodeOfOrigin: product.countryCodeOfOrigin || null,

    // Suppliers
    defaultSupplierId: product.defaultSupplier?.id || null,
    producerId: product.producer?.id || null,
    deliverToSupplierId: product.deliverToSupplier?.id || null,

    // Odoo
    odooTemplateId: product.odooTemplateId,
    odooId: product.odooId,
    odooIdInc: product.odooIdInc,
    odooCategoryId: product.odooCategoryId,

    packing: product.packing,
    packingName: product.packingName,

    size: {
      shape: product.shape,
      ballDiameter: product.ballDiameter,
      cubeX: product.cubeX,
      cubeY: product.cubeY,
      cubeZ: product.cubeZ,
      flatX: product.flatX,
      flatY: product.flatY,
      cylindricalDiameter: product.cylindricalDiameter,
      cylindricalHeight: product.cylindricalHeight,
      multiSizes: product.multiSizes,
    },

    itemWeight: product.itemWeight,
    grossWeight: product.grossWeight,
    productMaterial: product.productMaterial,

    innerBoxX: product.innerBoxX,
    innerBoxY: product.innerBoxY,
    innerBoxZ: product.innerBoxZ,
    displayBoxX: product.displayBoxX,
    displayBoxY: product.displayBoxY,
    displayBoxZ: product.displayBoxZ,
    innerCartonX: product.innerCartonX,
    innerCartonY: product.innerCartonY,
    innerCartonZ: product.innerCartonZ,
    outerCartonX: product.outerCartonX,
    outerCartonY: product.outerCartonY,
    outerCartonZ: product.outerCartonZ,
    quantityPerDisplayBox: product.quantityPerDisplayBox,
    quantityPerCarton: product.quantityPerCarton,
    ctnNetWeight: product.ctnNetWeight,
    ctnGrossWeight: product.ctnGrossWeight,
    outerCartonCbm: product.outerCartonCbm,

    moq: product.moq,
    barCode: product.barCode,
    defaultQty: product.defaultQty,
    itemsPerSet: product.itemsPerSet,

    isActive: product.isActive,
    isActiveForBiz: product.isActiveForBiz,
    isSalable: product.isSalable,
    isPurchasable: product.isPurchasable,

    shippingMarkName: product.shippingMarkName,
    colors: product.colors,

    outerCartonBarcodeEan: product.outerCartonBarcodeEan,
    outerCartonBarcodeUpc: product.outerCartonBarcodeUpc,
    innerCartonBarcodeEan: product.innerCartonBarcodeEan,
    innerCartonBarcodeUpc: product.innerCartonBarcodeUpc,
    innerBoxBarcodeEan: product.innerBoxBarcodeEan,
    innerBoxBarcodeUpc: product.innerBoxBarcodeUpc,
    innerBoxBarcodeJd: product.innerBoxBarcodeJd,
    outerCartonBarcodeJd: product.outerCartonBarcodeJd,

    cccInfo: product.cccInfo,

    reminderForMissingBeautyShot: product.reminderForMissingBeautyShot,

    considerBomInProduction: product.considerBomInProduction,
    useProductLineImagesIfNoImages: product.useProductLineImagesIfNoImages,

    // Productions
    productionId: product.production?.id || null,
    productionCategoryId: product.productionCategory?.id || null,

    warningIds: product.warningIds,
    labTestStandardIds: product.labTestStandardIds,
    pantone: product.pantone,

    // Related objects
    productLineId: product.productLine?.id || null,
    productListId: product.productList?.id || null,
    gtmProject: product.gtmProject,

    // Cost
    activePrices: product.activePrices.map((i) => ({
      id: i.id,
      company: i.company,
      currency: i.currency,
      price: i.price,
      minQty: i.minQty,
      supplierUpdatedAt: i.supplierUpdatedAt,
      archivedAt: i.archivedAt,
      archivedCode: i.archivedCode,
      archivedNotes: i.archivedNotes,
      factoryId: i.factory?.id || null,
      factoryOdooId: i.factory?.odooId || null,
    })),
    costSuppliers: product.costSuppliers.map((i) => ({
      useType: "COST",
      company: i.company,
      supplierId: i.supplier?.id || null,
    })),

    // BOM
    children: product.children.map((i) => ({
      product: i.product,
      qty: i.qty,
    })),

    // Compliance
    complianceCertificates: product.complianceCertificates || [],
    labTestReports: product.labTestReports || [],
  };

  return (
    <EditProductView
      id={id}
      initialValues={initialValues}
      hide={hide}
      onSaved={onSaved}
      duplicateProduct={duplicateProduct}
    />
  );
};

export default ProductLoader;
